import { Link, useLocation, useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth } from "../context/AuthContext";
import Logo from "./Logo";
import { LINKS } from "../lib/links";
import { Menu, X, Shield, LogOut } from "lucide-react";

const NAV = [
  { to: "/", label: "Home" },
  { to: "/watch", label: "Watch More" },
  { to: "/resources", label: "Library" },
  { to: "/tools", label: "Tools" },
  { to: "/community", label: "Community" },
  { to: "/newsletter", label: "Newsletter" },
  { to: "/business", label: "For Business" },
];

const slug = (s) => s.toLowerCase().replace(/\s+/g, "-");

export default function Navbar() {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const loc = useLocation();
  const nav = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const isActive = (to) => (to === "/" ? loc.pathname === "/" : loc.pathname.startsWith(to));

  return (
    <header className="fixed top-0 inset-x-0 z-50 px-4 pt-4" data-testid="navbar">
      <div className={`max-w-6xl mx-auto rounded-2xl transition-all duration-500 ${scrolled || open ? "glass" : "bg-transparent"}`}>
        <div className="px-5 md:px-7 h-14 flex items-center justify-between">
          <Link to="/" className="flex items-center group" data-testid="navbar-logo">
            <Logo size={30} textClassName="text-[15px]" />
          </Link>

          <nav className="hidden xl:flex flex-1 justify-center items-center gap-0.5 px-4">
            {NAV.map((n) => {
              const active = isActive(n.to);
              return (
                <Link key={n.to} to={n.to} data-testid={`nav-${slug(n.label)}`}
                  className={`relative px-3 py-2 text-[13px] rounded-full transition-colors whitespace-nowrap ${active ? "text-white" : "text-zinc-500 hover:text-white"}`}>
                  {active && (
                    <motion.span layoutId="nav-pill" className="absolute inset-0 rounded-full bg-white/[0.07] border border-white/10"
                      transition={{ type: "spring", stiffness: 380, damping: 30 }} />
                  )}
                  <span className="relative z-10">{n.label}</span>
                </Link>
              );
            })}
          </nav>

          <div className="hidden xl:flex items-center gap-1 shrink-0">
            {user ? (
              <>
                <Link to="/dashboard" data-testid="nav-dashboard" className="text-[13px] text-zinc-400 hover:text-white px-3 py-2 transition-colors">Sanctuary</Link>
                <Link to="/journal" data-testid="nav-journal" className="text-[13px] text-zinc-400 hover:text-white px-3 py-2 transition-colors">Journal</Link>
                {user.role === "admin" && (
                  <Link to="/admin" data-testid="nav-admin" className="text-[13px] text-[#4da6ff] hover:text-white px-3 py-2 flex items-center gap-1 transition-colors">
                    <Shield className="w-3.5 h-3.5" /> Admin
                  </Link>
                )}
                <button onClick={() => { logout(); nav("/"); }} data-testid="nav-logout" className="text-zinc-500 hover:text-white p-2 transition-colors">
                  <LogOut className="w-4 h-4" />
                </button>
              </>
            ) : (
              <>
                <Link to="/login" data-testid="nav-login" className="text-[13px] text-zinc-400 hover:text-white px-4 py-2 transition-colors">Sign in</Link>
                <Link to="/register" data-testid="nav-register" className="btn-primary text-[13px] font-medium px-5 py-2">Begin Your Journey</Link>
              </>
            )}
          </div>

          <button className="xl:hidden text-white" onClick={() => setOpen(!open)} data-testid="mobile-menu-toggle">
            {open ? <X /> : <Menu />}
          </button>
        </div>

        <AnimatePresence>
          {open && (
            <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }}
              className="xl:hidden overflow-hidden border-t border-white/5 px-6 py-4 space-y-1" data-testid="mobile-menu">
              {NAV.map((n) => (
                <Link key={n.to} to={n.to} onClick={() => setOpen(false)} data-testid={`mobile-nav-${slug(n.label)}`} className="block py-2.5 text-zinc-300">{n.label}</Link>
              ))}
              <a href={`mailto:${LINKS.contactEmail}`} data-testid="mobile-nav-contact-email" className="block py-2.5 text-[#4da6ff] text-sm">{LINKS.contactEmail}</a>
              {user ? (
                <>
                  <Link to="/dashboard" onClick={() => setOpen(false)} className="block py-2.5 text-zinc-300">Sanctuary</Link>
                  <Link to="/journal" onClick={() => setOpen(false)} className="block py-2.5 text-zinc-300">Journal</Link>
                  {user.role === "admin" && <Link to="/admin" onClick={() => setOpen(false)} className="block py-2.5 text-[#4da6ff]">Admin</Link>}
                  <button onClick={() => { logout(); setOpen(false); nav("/"); }} className="block py-2.5 text-zinc-500">Sign out</button>
                </>
              ) : (
                <div className="flex gap-3 pt-3">
                  <Link to="/login" onClick={() => setOpen(false)} className="flex-1 text-center py-2.5 btn-glass text-sm">Sign in</Link>
                  <Link to="/register" onClick={() => setOpen(false)} className="flex-1 text-center py-2.5 btn-primary text-sm font-medium">Begin</Link>
                </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </header>
  );
}
